import { FilePenLine, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ehFisica,
  enderecoEmLinha,
  formatarCnae,
  formatarCnpj,
  formatarCpf,
  formatarInteiro,
  NOME_ENQUADRAMENTO,
  NOME_TIPO_SOCIETARIO,
  nomeDe,
  podeGerarAto,
  quotasSubscritas,
  rotuloDoAto,
  temImpeditivo,
  termoDaUnidade,
  valorParticipacaoCentavos,
  type Achado,
  type Administrador,
  type Ato,
  type Pagina,
  type Participacao,
  type Pessoa,
  type SituacaoAto,
  type Sociedade as TipoSociedade,
} from '@social/shared';
import { Abas, type Aba } from '../componentes/abas';
import { CartaoKpi } from '../componentes/cartao-kpi';
import { PaginaShell } from '../componentes/pagina-shell';
import { PainelLaudo } from '../componentes/painel-laudo';
import { Tabela, type Coluna } from '../componentes/tabela';
import { Botao, Etiqueta } from '../componentes/ui';
import { baixarDocumento } from '../lib/baixar';
import { api, useSessao } from '../lib/sessao';
import {
  classeSeloTipo,
  cnpjBR,
  dataBR,
  moeda,
  percentuaisVigentes,
  semRegistro,
  sociosAtuais,
} from './sociedade-comum';

interface Laudo {
  achados: Achado[];
}

interface DocumentoGerado {
  id: string;
  formato: 'DOCX' | 'PDF';
  bytes: number;
}

/**
 * Tom do selo de situação do ato — decoração, não regra: o servidor continua
 * sendo a única fonte da situação em si.
 */
const TOM_SITUACAO: Record<SituacaoAto, 'neutro' | 'ok' | 'alerta' | 'erro'> = {
  RASCUNHO: 'neutro',
  // VALIDADO e GERADO só existem depois de `temImpeditivo(...)` dar falso —
  // a MESMA condição que o PainelLaudo, na mesma tela, pinta de verde com
  // "Nada a corrigir. O contrato pode ser gerado." (ver atos.service.ts:108
  // e SITUACOES_QUE_GERAM em documentos.service.ts). Dois selos, um
  // significado, cor tinha de ser a mesma.
  VALIDADO: 'ok',
  GERADO: 'ok',
  ASSINADO: 'ok',
  PROTOCOLADO: 'ok',
  REGISTRADO: 'ok',
  EXIGENCIA: 'alerta',
  CANCELADO: 'erro',
};

const ABAS: readonly Aba[] = [
  { id: 'resumo', rotulo: 'Resumo' },
  { id: 'socios', rotulo: 'Sócios' },
  { id: 'administracao', rotulo: 'Administração' },
  { id: 'atos', rotulo: 'Atos' },
];

/** Nome do arquivo para o "salvar como" do navegador. */
function nomeArquivo(ato: Ato, formato: string): string {
  const rotulo = rotuloDoAto(ato.tipo, ato.sequencia)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-');
  return `${rotulo}.${formato.toLowerCase()}`;
}

/** Nome de quem consta no vínculo. `undefined` quando a leva de pessoas ainda não resolveu o id. */
function nomePessoa(p: Pessoa | undefined): string {
  return p ? nomeDe(p) : '—';
}

/** CPF para pessoa física, CNPJ para jurídica — o documento que a Junta pede na tabela. */
function documentoDe(p: Pessoa | undefined): string {
  if (!p) return '—';
  return ehFisica(p) ? formatarCpf(p.cpf) : formatarCnpj(p.cnpj);
}

export function Sociedade(): JSX.Element {
  const { id = '' } = useParams();
  const papel = useSessao((s) => s.papel);
  const fila = useQueryClient();
  const [achados, setAchados] = useState<Achado[]>([]);
  const [conferido, setConferido] = useState(false);
  // `atos`, não `resumo`: é a aba que já existia antes desta entrega, e é
  // onde vivem Conferir/Gerar/Baixar — abrir a tela numa aba diferente faria
  // quem já usa o produto (e o e2e que testa essa rotina) procurar os botões.
  const [aba, setAba] = useState('atos');

  const sociedade = useQuery({
    queryKey: ['sociedade', id],
    queryFn: () => api.get<TipoSociedade>(`/sociedades/${id}`),
  });

  /**
   * A leva de pessoas resolve nome e documento de sócios e administradores.
   *
   * `Sociedade.socios`/`administradores` só guardam `pessoaId` — é o estado
   * jurídico, não um cache de nome (ver o comentário de `SociedadeResumo`).
   * Mesma leva de 200 que a consulta usa: a API não tem `GET /pessoas/:id`,
   * e criar um por vínculo aqui reintroduziria o N+1 que o projeto já evita
   * na consulta.
   */
  const pessoas = useQuery({
    queryKey: ['pessoas', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Pessoa>>('/pessoas?tamanho=200'),
  });

  const pessoaPorId = useMemo(() => {
    const mapa = new Map<string, Pessoa>();
    for (const p of pessoas.data?.itens ?? []) mapa.set(p.id, p);
    return mapa;
  }, [pessoas.data]);

  const atos = useQuery({
    queryKey: ['atos', id],
    queryFn: () => api.get<Ato[]>(`/sociedades/${id}/atos`),
  });

  const ato = atos.data?.[0];

  const documentos = useQuery({
    queryKey: ['documentos', ato?.id],
    queryFn: () => api.get<DocumentoGerado[]>(`/atos/${ato?.id ?? ''}/documentos`),
    enabled: Boolean(ato),
  });

  const abrir = useMutation({
    mutationFn: () => api.post<Ato>(`/sociedades/${id}/constituicao`),
    onSuccess: () => void fila.invalidateQueries({ queryKey: ['atos', id] }),
  });

  const conferir = useMutation({
    mutationFn: (atoId: string) => api.post<Laudo>(`/atos/${atoId}/validar`),
    onSuccess: (laudo) => {
      setAchados(laudo.achados);
      setConferido(true);
      void fila.invalidateQueries({ queryKey: ['atos', id] });
    },
  });

  const gerar = useMutation({
    mutationFn: (atoId: string) =>
      api.post<DocumentoGerado[]>(`/atos/${atoId}/documentos`),
    /*
      Semeia o cache com o que o SERVIDOR devolveu, em vez de invalidar e
      esperar o refetch.

      O `POST` já responde com os dois documentos — pedir de volta é uma viagem
      à rede para buscar o que se acabou de receber. Pior: a busca corria com a
      invalidação de `['atos', id]`, e os botões "Baixar" só apareciam depois
      das duas voltarem. No e2e isso estourava os 5s de espera; para o contador
      era um piscar sem explicação.

      `atoId` vem do ARGUMENTO da mutação, não do `ato` do render: a chave tem
      de ser a do ato que acabou de gerar, e não a que estava em tela quando o
      componente montou.
    */
    onSuccess: (docs, atoId) => {
      fila.setQueryData(['documentos', atoId], docs);
      void fila.invalidateQueries({ queryKey: ['atos', id] });
    },
  });

  /**
   * O portão da interface, espelhando o do servidor.
   *
   * Habilita só depois de conferir E sem nenhum impeditivo. A API recusaria de
   * qualquer forma — a tela não é a defesa, é a cortesia: o contador descobre
   * que não pode gerar ANTES de clicar.
   */
  const podeGerar =
    Boolean(ato) &&
    conferido &&
    !temImpeditivo(achados) &&
    papel !== null &&
    podeGerarAto(papel);

  const s = sociedade.data;

  return (
    <PaginaShell
      titulo={s?.nomeEmpresarial ?? 'Carregando…'}
      subtitulo={
        s && (
          <span className="flex flex-wrap items-center gap-2">
            <span className={classeSeloTipo(s.tipo)} title={NOME_TIPO_SOCIETARIO[s.tipo]}>
              {s.tipo}
            </span>
            <Etiqueta>{NOME_ENQUADRAMENTO[s.enquadramento]}</Etiqueta>
            {semRegistro(s) ? (
              <Etiqueta tom="alerta">Sem registro na Junta</Etiqueta>
            ) : (
              <Etiqueta>{cnpjBR(s.cnpj)}</Etiqueta>
            )}
            {s.nire && <Etiqueta>NIRE {s.nire}</Etiqueta>}
          </span>
        )
      }
      indicadores={
        s && (
          <>
            <CartaoKpi rotulo="Capital social" valor={moeda(s.capitalCentavos)} />
            <CartaoKpi rotulo="Sócios" valor={String(sociosAtuais(s))} />
            <CartaoKpi
              rotulo="Quotas subscritas"
              valor={formatarInteiro(quotasSubscritas(s))}
            />
            <CartaoKpi rotulo="Início de atividades" valor={dataBR(s.inicioAtividades)} />
          </>
        )
      }
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_22rem] lg:items-start">
        <section className="flex min-w-0 flex-col gap-3">
          <Abas abas={ABAS} ativa={aba} aoEscolher={setAba} />

          {s && aba === 'resumo' && <ResumoAba sociedade={s} />}

          {s && aba === 'socios' && <SociosAba sociedade={s} pessoaPorId={pessoaPorId} />}

          {s && aba === 'administracao' && (
            <AdministracaoAba sociedade={s} pessoaPorId={pessoaPorId} />
          )}

          {aba === 'atos' && (
            <>
              <h2 className="text-tinta-900 text-lg font-semibold">Atos</h2>

              {atos.data?.length === 0 && papel && podeGerarAto(papel) && (
                <Botao
                  className="self-start"
                  carregando={abrir.isPending}
                  onClick={() => abrir.mutate()}
                >
                  {abrir.isPending ? 'Abrindo…' : 'Abrir contrato social'}
                </Botao>
              )}

              {/*
                A alteração só aparece DEPOIS da constituição: é o que o
                servidor exige (`sem_constituicao`), e oferecer o caminho antes
                levaria a pessoa a preencher a tela inteira para receber uma
                recusa no fim.
              */}
              {(atos.data?.length ?? 0) > 0 && papel && podeGerarAto(papel) && (
                <Link
                  to={`/sociedades/${id ?? ''}/alterar`}
                  className="border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 self-start rounded-lg border px-3 text-sm whitespace-nowrap transition-colors"
                >
                  <FilePenLine className="size-4" aria-hidden />
                  Nova alteração contratual
                </Link>
              )}

              {/*
                Os outros quatro atos. Como a alteração, só aparecem DEPOIS da
                constituição — é o que o servidor exige (`sem_constituicao`), e
                oferecer antes levaria a pessoa a preencher a tela inteira para
                receber uma recusa no fim.
              */}
              {(atos.data?.length ?? 0) > 0 && papel && podeGerarAto(papel) && (
                <Link
                  to={`/sociedades/${id ?? ''}/ato-novo`}
                  className="border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 self-start rounded-lg border px-3 text-sm whitespace-nowrap transition-colors"
                >
                  <FileText className="size-4" aria-hidden />
                  Distrato, procuração, ata ou transformação
                </Link>
              )}

              {atos.data?.length === 0 && papel && !podeGerarAto(papel) && (
                <p className="text-tinta-500 text-sm">
                  Abrir e gerar ato é do contador — o documento é assinado por quem tem
                  registro no CRC.
                </p>
              )}

              <ul className="flex flex-col gap-3">
                {atos.data?.map((a) => (
                  <li
                    key={a.id}
                    className="border-tinta-100 bg-superficie rounded-xl border p-4"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <strong className="text-tinta-900 font-medium">
                        {rotuloDoAto(a.tipo, a.sequencia)}
                      </strong>
                      <Etiqueta tom={TOM_SITUACAO[a.situacao]}>{a.situacao}</Etiqueta>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Botao
                        variante="secundaria"
                        carregando={conferir.isPending}
                        onClick={() => conferir.mutate(a.id)}
                      >
                        {conferir.isPending ? 'Conferindo…' : 'Conferir'}
                      </Botao>

                      <Botao
                        disabled={!podeGerar}
                        carregando={gerar.isPending}
                        onClick={() => gerar.mutate(a.id)}
                        title={
                          podeGerar
                            ? undefined
                            : 'Confira o ato e corrija o que impede antes de gerar.'
                        }
                      >
                        {gerar.isPending ? 'Gerando…' : 'Gerar documento'}
                      </Botao>

                      {documentos.data?.map((d) => (
                        <Botao
                          key={d.id}
                          variante="secundaria"
                          onClick={() =>
                            void baixarDocumento(d.id, nomeArquivo(a, d.formato))
                          }
                        >
                          Baixar .{d.formato.toLowerCase()}
                        </Botao>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <PainelLaudo
          achados={achados}
          carregando={conferir.isPending}
          conferido={conferido}
        />
      </div>
    </PaginaShell>
  );
}

/** Resumo: identificação que não é KPI nem tabela — texto corrido e endereço. */
function ResumoAba({ sociedade: s }: { sociedade: TipoSociedade }) {
  const principal = s.atividades.find((a) => a.principal) ?? s.atividades[0];
  const secundarias = s.atividades.filter((a) => a !== principal);

  return (
    <dl className="border-tinta-100 bg-superficie flex flex-col gap-4 rounded-xl border p-4">
      <div>
        <dt className="text-tinta-500 text-xs tracking-wide uppercase">Nome fantasia</dt>
        <dd className="text-tinta-900 mt-0.5 text-sm">{s.nomeFantasia ?? '—'}</dd>
      </div>

      <div>
        <dt className="text-tinta-500 text-xs tracking-wide uppercase">Objeto social</dt>
        <dd className="text-tinta-900 mt-0.5 text-sm">{s.objetoSocial}</dd>
      </div>

      <div>
        <dt className="text-tinta-500 text-xs tracking-wide uppercase">Endereço</dt>
        <dd className="text-tinta-900 mt-0.5 text-sm">{enderecoEmLinha(s.sede)}</dd>
      </div>

      <div>
        <dt className="text-tinta-500 text-xs tracking-wide uppercase">CNAEs</dt>
        <dd className="mt-1 flex flex-col gap-1.5">
          {principal && (
            <div className="flex items-center gap-2 text-sm">
              <Etiqueta tom="ok">Principal</Etiqueta>
              <span className="text-tinta-900">
                {formatarCnae(principal.cnae)} — {principal.descricao}
              </span>
            </div>
          )}
          {secundarias.map((a) => (
            <div key={a.cnae} className="text-tinta-700 pl-[4.5rem] text-sm">
              {formatarCnae(a.cnae)} — {a.descricao}
            </div>
          ))}
        </dd>
      </div>
    </dl>
  );
}

/**
 * Sócios: quotas, percentual e valor da participação — os dois últimos
 * DERIVADOS, nunca coluna do banco (é a regra de ouro do domínio).
 */
function SociosAba({
  sociedade: s,
  pessoaPorId,
}: {
  sociedade: TipoSociedade;
  pessoaPorId: ReadonlyMap<string, Pessoa>;
}) {
  const percentuais = useMemo(() => percentuaisVigentes(s), [s]);

  const colunas: Coluna<Participacao>[] = [
    {
      chave: 'nome',
      titulo: 'Nome',
      papel: 'titulo',
      celula: (p) => nomePessoa(pessoaPorId.get(p.pessoaId)),
    },
    {
      chave: 'cpf',
      titulo: 'CPF',
      celula: (p) => documentoDe(pessoaPorId.get(p.pessoaId)),
    },
    {
      chave: 'quotas',
      titulo: termoDaUnidade(s.tipo).plural.replace(/^./, (c) => c.toUpperCase()),
      alinhamento: 'direita',
      celula: (p) => <span className="tabular-nums">{formatarInteiro(p.quotas)}</span>,
    },
    {
      chave: 'percentual',
      titulo: 'Percentual',
      alinhamento: 'direita',
      celula: (p) => (
        <span className="tabular-nums">
          {(percentuais.get(p.pessoaId) ?? 0).toFixed(2).replace('.', ',')}%
        </span>
      ),
    },
    {
      chave: 'valor',
      titulo: 'Valor da participação',
      alinhamento: 'direita',
      celula: (p) => {
        // `null` = ação SEM VALOR NOMINAL (art. 11 da Lei 6.404/76). Escrever
        // "R$ 0,00" diria que a participação não vale nada, quando o que
        // acontece é que ela não tem valor unitário fixado no estatuto.
        const valor = valorParticipacaoCentavos(s, p);
        return (
          <span className="tabular-nums">{valor === null ? '—' : moeda(valor)}</span>
        );
      },
    },
    {
      chave: 'entrada',
      titulo: 'Entrada',
      celula: (p) => dataBR(p.entrada),
    },
    {
      chave: 'saida',
      titulo: 'Saída',
      celula: (p) => (p.saida ? dataBR(p.saida) : '—'),
    },
  ];

  return (
    <Tabela
      colunas={colunas}
      pagina={{ itens: s.socios, total: s.socios.length, pagina: 1, totalPaginas: 1 }}
      carregando={false}
      chaveDe={(p) => `${p.pessoaId}-${p.entrada}`}
      aoTrocarPagina={() => undefined}
      vazio={{
        titulo: 'Nenhum sócio cadastrado.',
        descricao: 'Isto não deveria acontecer — toda sociedade tem ao menos um sócio.',
      }}
    />
  );
}

/** Administração: quem administra, o período do mandato e o alcance da assinatura. */
function AdministracaoAba({
  sociedade: s,
  pessoaPorId,
}: {
  sociedade: TipoSociedade;
  pessoaPorId: ReadonlyMap<string, Pessoa>;
}) {
  const colunas: Coluna<Administrador>[] = [
    {
      chave: 'nome',
      titulo: 'Administrador',
      papel: 'titulo',
      celula: (a) => nomePessoa(pessoaPorId.get(a.pessoaId)),
    },
    {
      chave: 'inicio',
      titulo: 'Desde quando',
      celula: (a) => dataBR(a.inicio),
    },
    {
      chave: 'fim',
      titulo: 'Até quando',
      celula: (a) => (a.fim ? dataBR(a.fim) : '—'),
    },
    {
      chave: 'assinatura',
      titulo: 'Assinatura',
      celula: (a) => (a.usoIsoladoDoNome ? 'Isolado' : 'Em conjunto'),
    },
  ];

  return (
    <Tabela
      colunas={colunas}
      pagina={{
        itens: s.administradores,
        total: s.administradores.length,
        pagina: 1,
        totalPaginas: 1,
      }}
      carregando={false}
      chaveDe={(a) => `${a.pessoaId}-${a.inicio}`}
      aoTrocarPagina={() => undefined}
      vazio={{
        titulo: 'Nenhum administrador cadastrado.',
        descricao:
          'Isto não deveria acontecer — toda sociedade tem ao menos um administrador.',
      }}
    />
  );
}
